/**
 * 账号与登录的调用点（`specs/15` §2）。
 *
 * 走 `client.ts`（唯一 fetch 出口）；登录成功后把 token 落到本地，
 * 此后所有请求由 `client.ts` 自动带 `Authorization: Bearer`。
 *
 * ⚠️ `view_mode`（当前身份，`ADR-025` D1）**不是权限**：它只回答"此刻按谁渲染"。
 * 权限由 `platform_role` 决定，后端判（`CONSTITUTION` A3）。
 */

import { clearToken, readToken, writeToken } from "@/lib/auth-token";

import { ApiError, api } from "./client";
import type { RoleChangeLog } from "./types";

export interface SessionUser {
  id: string;
  email: string;
  display_name: string;
  platform_role: "player" | "editor";
  creation_role: string | null;
  /** 当前身份（`ADR-025` D1）：本人可随时切；**不参与权限判定**。 */
  view_mode: "player" | "editor";
  status: string;
}

interface LoginResponse {
  token: string;
  user: SessionUser;
}

export async function login(email: string, password: string): Promise<SessionUser> {
  const data = await api.post<LoginResponse>("/auth/login", { email, password });
  writeToken(data.token);
  return data.user;
}

export async function register(
  email: string,
  password: string,
  displayName: string,
): Promise<SessionUser> {
  const data = await api.post<LoginResponse>("/auth/register", {
    email,
    password,
    display_name: displayName,
  });
  writeToken(data.token);
  return data.user;
}

/**
 * 会话恢复：带 token 时读 `GET /auth/me`；无 token 直接返回 `null`（不是错误）。
 *
 * - token 失效/过期（401）→ 清掉本地 token 并返回 `null`（等于未登录），**不静默降级成假身份**；
 * - 网络/协议错误照常抛出，由调用方（`useSession` 的消费方）呈现错误态。
 */
export async function me(): Promise<SessionUser | null> {
  if (!readToken()) return null;
  try {
    return await api.get<SessionUser>("/auth/me");
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      clearToken();
      return null;
    }
    throw error;
  }
}

/**
 * 退出登录：先请服务端撤销会话（`POST /auth/logout`），再清本地 token。
 *
 * 撤销失败不阻塞本地登出——旧 token 下次请求会被服务端判 401，同样收敛到"已登出"。
 */
export async function logout(): Promise<void> {
  try {
    await api.post<void>("/auth/logout");
  } catch {
    // 尽力而为：服务端撤销失败（网络/已失效）不应让用户被困在登录态。
  } finally {
    clearToken();
  }
}

/**
 * 切换**当前身份**（`POST /auth/me/switch-view`，`ADR-025` D2）。
 *
 * - 越授权（未开通编辑身份却要切到 `editor`）→ 后端 `403 ROLE_NOT_GRANTED`，
 *   本层**原样抛出**，不静默改成"切到玩家"——那会让用户以为自己升上了（CONSTITUTION B7）；
 * - 切到「观众」**不降权**：创作权限在数据层保留，切回即恢复（`ADR-025` D1）。
 */
export async function switchView(viewMode: "player" | "editor"): Promise<SessionUser> {
  return api.post<SessionUser>("/auth/me/switch-view", { view_mode: viewMode });
}

/** admin 设置任意账号的授权（`POST /auth/users/{id}/set-role`，`ADR-025` D3）。可升可降。 */
export async function setUserRole(
  userId: string,
  input: { platform_role?: "player" | "editor"; creation_role?: string; reason?: string },
): Promise<SessionUser> {
  return api.post<SessionUser>(`/auth/users/${userId}/set-role`, input);
}

/** 角色变更记录（`GET /auth/role-changes`，`ADR-025` D5）。

 * 不传 `user_id` 时：admin 得全量，其他人得**本人**——归属由服务端写死在查询里。 */
export async function fetchRoleChangeLogs(userId?: string): Promise<RoleChangeLog[]> {
  return api.get<RoleChangeLog[]>("/auth/role-changes", {
    query: userId ? { user_id: userId } : undefined,
  });
}
